import type { Site, SiteStreetView as SiteStreetViewDto } from '@bk/contracts';
import { useTenantQuery } from '../../../lib/useTenant';

/** Indirizzo completo di una sede per la mappa; null se non c'è abbastanza per trovarla. */
export function siteAddress(s: Site): string | null {
  const parts = [
    s.address,
    s.postal_code,
    s.municipality,
    s.province,
    s.country ?? (s.municipality ? 'Italia' : null),
  ].filter(Boolean);
  return s.address || s.municipality ? parts.join(', ') : null;
}

/**
 * La sede vista dalla strada, a filo del suo contenitore: niente bordi, collegamenti né indirizzo, solo la vista e i
 * comandi per muoversi. Il server chiede a Google (ADR 0028) il punto dell'edificio e la foto più vicina scattata dalla
 * strada, girata verso l'edificio; se non c'è una foto resta la mappa.
 */
export function SiteStreetView({ site, className = '' }: { site: Site; className?: string }) {
  const address = siteAddress(site);
  const view = useTenantQuery<SiteStreetViewDto>(
    ['sites', site.id, 'street-view', address],
    `/sites/${site.id}/street-view`,
    { enabled: !!address },
  );
  if (!address)
    return (
      <div
        className={`flex items-center justify-center bg-black/[0.03] p-4 text-center text-xs text-ink/50 ${className}`}
      >
        Con l’indirizzo qui vedi la sede dalla strada
      </div>
    );
  if (view.isLoading) return <div className={`animate-pulse bg-black/[0.04] ${className}`} />;
  const v = view.data?.vista;
  const p = view.data?.punto;
  const src = v
    ? `https://maps.google.com/maps?layer=c&cbll=${v.lat},${v.lng}&cbp=12,${v.direzione},0,0,0&hl=it&output=svembed`
    : `https://maps.google.com/maps?q=${p ? `${p.lat},${p.lng}` : encodeURIComponent(address)}&hl=it&z=17&output=embed`;
  // Google mette in alto a sinistra un riquadro con indirizzo e collegamenti che non si può togliere: l'iframe sale
  // di CROP pixel dentro un contenitore che li taglia, e restano la vista e i comandi per muoversi.
  return (
    <div className={`relative overflow-hidden ${className}`}>
      <iframe
        title={`${site.name} vista dalla strada`}
        className="absolute inset-x-0 block w-full border-0"
        style={{ top: -CROP, height: `calc(100% + ${CROP}px)` }}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        src={src}
      />
    </div>
  );
}

/** Altezza del riquadro di Google con l'indirizzo, in cima a Street View e alla mappa incorporata. */
const CROP = 90;
